import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMsal } from '@azure/msal-react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Radio,
  RadioGroup,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DatasetIcon from "@mui/icons-material/Dataset";
import ErrorIcon from "@mui/icons-material/Error";
import FilterListIcon from "@mui/icons-material/FilterList";
import InsightsIcon from "@mui/icons-material/Insights";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SearchIcon from "@mui/icons-material/Search";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Navbar from "../components/Navbar.js";
import { backendRoot } from "../config";

const pagePalette = {
  background: "#F5F7FB",
  surface: "#FFFFFF",
  ink: "#172033",
  muted: "#667085",
  border: "#DDE7F0",
  primary: "#22577A",
  primaryLight: "#E7F4FF",
  accent: "#38A3A5",
  danger: "#E5484D",
  success: "#2E7D59",
};

const secondaryButtonSx = {
  color: pagePalette.primary,
  borderColor: "#BBD7EA",
  bgcolor: "#fff",
  fontWeight: 700,
  borderRadius: 1.5,
  px: 2,
  textTransform: "none",
  "&:hover": { borderColor: pagePalette.primary, bgcolor: pagePalette.primaryLight },
};

const primaryButtonSx = {
  bgcolor: pagePalette.primary,
  color: "#fff",
  fontWeight: 700,
  borderRadius: 1.5,
  px: 2.5,
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { bgcolor: "#1B4863", boxShadow: "none" },
};

const wizardSteps = ["Project details", "Upload records", "Process"];

function MetricCard({ icon, label, value, color = pagePalette.primary, helper }) {
  return (
    <Card sx={{
      minWidth: 210,
      flex: 1,
      borderRadius: 2,
      border: `1px solid ${pagePalette.border}`,
      boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)",
      background: pagePalette.surface,
      overflow: "hidden",
    }}>
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
          <Box>
            <Typography sx={{ color: pagePalette.muted, fontSize: "0.78rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0 }}>
              {label}
            </Typography>
            <Typography sx={{ color: pagePalette.ink, fontSize: "1.75rem", lineHeight: 1.2, fontWeight: 800, mt: 0.5 }}>
              {value}
            </Typography>
            {helper && (
              <Typography sx={{ color: pagePalette.muted, fontSize: "0.78rem", mt: 0.5 }}>
                {helper}
              </Typography>
            )}
          </Box>
          <Box sx={{
            width: 42,
            height: 42,
            borderRadius: 1.5,
            display: "grid",
            placeItems: "center",
            bgcolor: `${color}18`,
            color,
          }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

const statusChipSx = (status) => {
  const map = {
    active: { bg: "#E7F6EC", fg: pagePalette.success },
    archived: { bg: "#EEF1F5", fg: pagePalette.muted },
  };
  const tone = map[status] || map.archived;
  return { bgcolor: tone.bg, color: tone.fg, fontWeight: 700, textTransform: "capitalize" };
};

export default function ProjectDetails() {
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const currentUser = accounts?.[0]?.name || accounts?.[0]?.username || '';
  const isClientUser = !!localStorage.getItem('clientToken');
  const allowedProjects = isClientUser ? JSON.parse(localStorage.getItem('allowedProjects') || '[]') : null;
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summaryMetrics, setSummaryMetrics] = useState({
    totalInputCount: 0,
    totalTickets: 0,
    totalFailed: 0,
    failedInDateRange: 0,
    successRate: 0,
  });

  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState(null);
  const [processingId, setProcessingId] = useState(null);
  const [toast, setToast] = useState(null);

  // ----- New Project wizard state -----
  const [wizardOpen, setWizardOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [wizardBusy, setWizardBusy] = useState(false);
  const [wizardError, setWizardError] = useState(null);
  const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [createdProject, setCreatedProject] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [processChoice, setProcessChoice] = useState("now");
  const [processingMode, setProcessingMode] = useState("tdp_bsw");
  const [projectToProcess, setProjectToProcess] = useState(null);
  const [projectProcessingMode, setProjectProcessingMode] = useState("as_saved");
  const [includeCompleted, setIncludeCompleted] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);

  const fetchSummaryMetrics = useCallback(async () => {
    setSummaryLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);

      const url = `${backendRoot}/api/summary-metrics/${params.toString() ? "?" + params.toString() : ""}`;
      const response = await fetch(url, { method: "GET", headers: { "Content-Type": "application/json" } });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      setSummaryMetrics({
        totalInputCount: data.total_input_count || 0,
        totalTickets: data.total_tickets || 0,
        totalFailed: data.total_failed || 0,
        failedInDateRange: data.failed_in_date_range || 0,
        successRate: data.success_rate || 0,
      });
    } catch (summaryError) {
      console.error("Error fetching project summary metrics:", summaryError);
      setError(`Failed to fetch project summary: ${summaryError.message}`);
    } finally {
      setSummaryLoading(false);
    }
  }, [startDate, endDate]);

  const fetchProjects = useCallback(async () => {
    setProjectsLoading(true);
    setProjectsError(null);
    try {
      const params = new URLSearchParams();
      params.append("status", "active");
      if (searchQuery.trim()) params.append("search", searchQuery.trim());
      const url = `${backendRoot}/api/projects/${params.toString() ? "?" + params.toString() : ""}`;
      const response = await fetch(url, { method: "GET", headers: { "Content-Type": "application/json" } });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      let allProjects = Array.isArray(data) ? data : data.results || [];
      if (allowedProjects && allowedProjects.length > 0) {
        allProjects = allProjects.filter(p => allowedProjects.includes(p.name));
      }
      setProjects(allProjects);
    } catch (projErr) {
      console.error("Error fetching projects:", projErr);
      setProjectsError(`Failed to load projects: ${projErr.message}`);
      setProjects([]);
    } finally {
      setProjectsLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    fetchSummaryMetrics().catch(() => {});
  }, [fetchSummaryMetrics]);

  // Debounce project search so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const handle = setTimeout(() => {
      fetchProjects().catch(() => {});
    }, 300);
    return () => clearTimeout(handle);
  }, [fetchProjects]);

  const handleClearFilters = () => {
    setStartDate("");
    setEndDate("");
    setSearchQuery("");
  };

  const refreshAll = useCallback(() => {
    fetchProjects().catch(() => {});
    fetchSummaryMetrics().catch(() => {});
  }, [fetchProjects, fetchSummaryMetrics]);

  // Enqueue every not-done record of an existing project.
  const handleProcessProject = (project, event) => {
    if (event) event.stopPropagation();
    setProjectProcessingMode("as_saved");
    setIncludeCompleted(false);
    setProjectToProcess(project);
  };

  const submitProjectProcessing = async () => {
    const project = projectToProcess;
    if (!project) return;
    setProcessingId(project.project_id);
    setToast(null);
    try {
      const response = await fetch(`${backendRoot}/api/projects/${project.project_id}/process/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...(projectProcessingMode === "as_saved" ? {} : { processing_mode: projectProcessingMode }),
          reprocess: includeCompleted,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || `HTTP error! status: ${response.status}`);
      setToast({
        severity: "success",
        message: `Queued ${data.queued_now} record(s) in "${project.name}" for processing.`,
      });
      setProjectToProcess(null);
      refreshAll();
    } catch (procErr) {
      setToast({ severity: "error", message: `Could not start processing: ${procErr.message}` });
    } finally {
      setProcessingId(null);
    }
  };

  // ----- Wizard handlers -----
  const openWizard = () => {
    setActiveStep(0);
    setWizardError(null);
    setNewName("");
    setNewDescription("");
    setCreatedProject(null);
    setSelectedFile(null);
    setUploadResult(null);
    setProcessChoice("now");
    setProcessingMode("tdp_bsw");
    setWizardOpen(true);
  };

  const closeWizard = () => {
    if (wizardBusy) return;
    setWizardOpen(false);
    // A project may have been created/populated mid-wizard — keep the list fresh.
    refreshAll();
  };

  const handleCreateProject = async () => {
    if (!newName.trim()) {
      setWizardError("Project name is required.");
      return;
    }
    setWizardBusy(true);
    setWizardError(null);
    try {
      const response = await fetch(`${backendRoot}/api/projects/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: newName.trim(),
          description: newDescription.trim(),
          created_by: currentUser,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || `HTTP error! status: ${response.status}`);
      setCreatedProject(data);
      setActiveStep(1);
    } catch (createErr) {
      setWizardError(createErr.message);
    } finally {
      setWizardBusy(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !createdProject) return;
    setWizardBusy(true);
    setWizardError(null);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("project_id", createdProject.project_id);
      formData.append("auto_queue", "false");
      formData.append("processing_mode", processingMode);
      const response = await fetch(`${backendRoot}/api/upload-excel-payloads/`, {
        method: "POST",
        body: formData,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || `HTTP error! status: ${response.status}`);
      setUploadResult(data);
    } catch (uploadErr) {
      setWizardError(uploadErr.message);
    } finally {
      setWizardBusy(false);
    }
  };

  const handleFinish = async () => {
    setWizardBusy(true);
    setWizardError(null);
    try {
      if (processChoice === "now" && createdProject) {
        const response = await fetch(`${backendRoot}/api/projects/${createdProject.project_id}/process/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ processing_mode: processingMode }),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || `HTTP error! status: ${response.status}`);
        setToast({
          severity: "success",
          message: `Project "${createdProject.name}" created and ${data.queued_now} record(s) queued.`,
        });
      } else {
        setToast({
          severity: "success",
          message: `Project "${createdProject?.name}" created. Records are saved and can be processed later.`,
        });
      }
      setWizardOpen(false);
      refreshAll();
    } catch (finishErr) {
      setWizardError(finishErr.message);
    } finally {
      setWizardBusy(false);
    }
  };

  const totalProjects = projects.length;
  const tableColumns = useMemo(
    () => ["Project ID", "Project Name", "Records", "Pending", "Completed", "Failed", "Status", "Created By", "Created At", "Actions"],
    []
  );

  return (
    <Box sx={{ background: pagePalette.background, minHeight: "100vh", color: pagePalette.ink }}>
      <Navbar />

      <Box sx={{ px: { xs: 2, md: 4 }, pt: 2, pb: 0 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.5, md: 2 },
            borderRadius: 2,
            border: `1px solid ${pagePalette.border}`,
            boxShadow: "0 14px 36px rgba(15, 23, 42, 0.07)",
            background: "linear-gradient(135deg, #FFFFFF 0%, #F6FBFF 58%, #F0FAF8 100%)",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "center" }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, color: pagePalette.ink, lineHeight: 1.2, fontSize: { xs: "1.1rem", md: "1.3rem" }, letterSpacing: "-0.02em" }}>
                Project Details
              </Typography>
              <Typography sx={{ color: pagePalette.muted, mt: 0.3, fontSize: "0.78rem" }}>
                Dashboard
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center", flex: 1, justifyContent: "center" }}>
              {[
                { icon: <DatasetIcon sx={{ fontSize: 14 }} />, label: "Input Records", value: summaryMetrics.totalInputCount || 0, color: pagePalette.primary },
                { icon: <InsightsIcon sx={{ fontSize: 14 }} />, label: "Total Tickets", value: summaryMetrics.totalTickets || 0, color: pagePalette.accent },
                // { icon: <ErrorIcon sx={{ fontSize: 14 }} />, label: "Failed", value: summaryMetrics.failedInDateRange || 0, color: pagePalette.danger },
                { icon: <TaskAltIcon sx={{ fontSize: 14 }} />, label: "Success Rate", value: summaryMetrics.successRate ? `${summaryMetrics.successRate}%` : "N/A", color: pagePalette.success },
              ].map(({ icon, label, value, color }) => (
                <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.75, px: 1.5, py: 0.6, borderRadius: 10, border: `1px solid ${pagePalette.border}`, bgcolor: `${color}0D` }}>
                  <Box sx={{ color, display: "flex", alignItems: "center" }}>{icon}</Box>
                  <Typography sx={{ fontSize: "0.7rem", color: pagePalette.muted, fontWeight: 700 }}>{label}:</Typography>
                  <Typography sx={{ fontSize: "0.82rem", fontWeight: 800, color: pagePalette.ink }}>{value}</Typography>
                </Box>
              ))}
            </Box>
            {!isClientUser && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={openWizard} sx={primaryButtonSx}>
                New Project
              </Button>
            )}
          </Box>
          <Box sx={{ mt: 1.5, pt: 1.25, borderTop: `1px solid ${pagePalette.border}` }}>
            <Box
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, cursor: "pointer", userSelect: "none", "&:hover": { opacity: 0.72 } }}
              onClick={() => setFilterOpen(!filterOpen)}
            >
              <FilterListIcon sx={{ color: pagePalette.primary, fontSize: 16 }} />
              <Typography sx={{ color: pagePalette.ink, fontWeight: 700, fontSize: "0.82rem" }}>Operational Filters</Typography>
              <ExpandMoreIcon sx={{ color: pagePalette.muted, fontSize: 18, transition: "transform 0.2s", transform: filterOpen ? "rotate(180deg)" : "rotate(0deg)" }} />
            </Box>
            {filterOpen && (
              <Box sx={{ mt: 1.5 }}>
                <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
                  <TextField
                    type="date"
                    label="Start Date"
                    value={startDate || ""}
                    onChange={(event) => setStartDate(event.target.value)}
                    size="small"
                    sx={{ minWidth: 160 }}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    type="date"
                    label="End Date"
                    value={endDate || ""}
                    onChange={(event) => setEndDate(event.target.value)}
                    size="small"
                    sx={{ minWidth: 160 }}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    placeholder="Search Project"
                    variant="outlined"
                    size="small"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    sx={{
                      minWidth: { xs: "100%", sm: 340 },
                      flex: 1,
                      "& .MuiOutlinedInput-root": {
                        bgcolor: "#fff",
                        "&:hover fieldset": { borderColor: pagePalette.primary },
                        "&.Mui-focused fieldset": { borderColor: pagePalette.primary },
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: pagePalette.primary }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button variant="outlined" onClick={handleClearFilters} sx={secondaryButtonSx}>
                    Clear Filter
                  </Button>
                </Box>
              </Box>
            )}

          </Box>
        </Paper>

      </Box>

      {(error || projectsError || toast) && (
        <Box sx={{ px: { xs: 2, md: 4 }, mb: 2, display: "flex", flexDirection: "column", gap: 1 }}>
          {error && (
            <Alert severity="error" action={<Button color="inherit" size="small" onClick={fetchSummaryMetrics}>RETRY</Button>}>
              {error}
            </Alert>
          )}
          {projectsError && (
            <Alert severity="error" action={<Button color="inherit" size="small" onClick={fetchProjects}>RETRY</Button>}>
              {projectsError}
            </Alert>
          )}
          {toast && (
            <Alert severity={toast.severity} onClose={() => setToast(null)}>
              {toast.message}
            </Alert>
          )}
        </Box>
      )}

      <Box sx={{ px: { xs: 2, md: 4 }, pt: 1, pb: 2 }}>
        <Paper elevation={0} sx={{ borderRadius: 2, overflow: "hidden", border: `1px solid ${pagePalette.border}`, boxShadow: "0 14px 32px rgba(15, 23, 42, 0.08)", width: '100%' }}>
          <Box sx={{ px: 2.5, py: 2, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, borderBottom: `1px solid ${pagePalette.border}`, bgcolor: pagePalette.surface }}>
            {projectsLoading ? <CircularProgress size={16} sx={{ color: pagePalette.primary }} /> : <Box sx={{ minWidth: 16 }} />}
            <Typography sx={{ fontWeight: 800, color: pagePalette.ink, flex: 1, textAlign: 'center' }}>Project Queue</Typography>
            <Chip
              label={`${totalProjects} project${totalProjects === 1 ? "" : "s"}`}
              size="small"
              sx={{ bgcolor: pagePalette.primaryLight, color: pagePalette.primary, fontWeight: 800, border: "1px solid #CFE2F3" }}
            />
          </Box>
          <TableContainer sx={{ maxHeight: "58vh", bgcolor: pagePalette.surface }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {tableColumns.map((heading) => (
                    <TableCell key={heading} sx={{ bgcolor: "#EEF2F7", color: pagePalette.ink, fontWeight: 800, py: 1.25 }}>
                      {heading}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {projects.length > 0 ? (
                  projects.map((project, index) => {
                    const busy = processingId === project.project_id;
                    return (
                      <TableRow
                        key={project.project_id}
                        hover
                        onClick={() => navigate(`/transactions?project_id=${project.project_id}`)}
                        sx={{
                          cursor: "pointer",
                          bgcolor: index % 2 === 0 ? "#FFFFFF" : "#F8FBFD",
                          "&:hover": { backgroundColor: "#EEF6FF" },
                          height: 44,
                          "& .MuiTableCell-root": {
                            borderBottom: `1px solid ${pagePalette.border}`,
                            color: pagePalette.ink,
                            fontSize: "0.86rem",
                          },
                        }}
                      >
                        <TableCell sx={{ fontWeight: 800 }}>{project.project_id}</TableCell>
                        <TableCell sx={{ fontWeight: 800, color: `${pagePalette.primary} !important` }}>{project.name}</TableCell>
                        <TableCell>{project.record_count ?? 0}</TableCell>
                        <TableCell sx={{ color: pagePalette.muted }}>{project.pending_count ?? 0}</TableCell>
                        <TableCell sx={{ color: pagePalette.success, fontWeight: 700 }}>{project.completed_count ?? 0}</TableCell>
                        <TableCell sx={{ color: pagePalette.danger, fontWeight: 700 }}>{project.failed_count ?? 0}</TableCell>
                        <TableCell>
                          <Chip label={project.status} size="small" sx={statusChipSx(project.status)} />
                        </TableCell>
                        <TableCell sx={{ color: pagePalette.muted }}>{project.created_by || "—"}</TableCell>
                        <TableCell sx={{ color: pagePalette.muted }}>
                          {project.created ? new Date(project.created).toLocaleString() : "—"}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Tooltip title="Process project records">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={busy || (project.record_count ?? 0) === 0}
                                  onClick={(event) => handleProcessProject(project, event)}
                                  sx={{ color: pagePalette.accent }}
                                >
                                  {busy ? <CircularProgress size={16} /> : <PlayArrowIcon fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Open records">
                              <IconButton
                                size="small"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  navigate(`/transactions?project_id=${project.project_id}`);
                                }}
                                sx={{ color: pagePalette.primary }}
                              >
                                <OpenInNewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={tableColumns.length} align="center">
                      <Box sx={{ py: 5 }}>
                        <Typography sx={{ color: pagePalette.ink, fontWeight: 800 }}>
                          {searchQuery ? `No projects found matching "${searchQuery}".` : "No projects yet."}
                        </Typography>
                        <Typography sx={{ color: pagePalette.muted, mt: 0.5 }}>
                          {searchQuery ? "Clear the project search to show all projects." : "Click \"New Project\" to create one and upload records."}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      <Dialog open={Boolean(projectToProcess)} onClose={() => !processingId && setProjectToProcess(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Process {projectToProcess?.name}</DialogTitle>
        <DialogContent dividers>
          <RadioGroup value={projectProcessingMode} onChange={(event) => setProjectProcessingMode(event.target.value)}>
            <FormControlLabel value="as_saved" control={<Radio />} label="Use saved mode" />
            <FormControlLabel value="tdp_only" control={<Radio />} label="TDP only" />
            <FormControlLabel value="bsw_only" control={<Radio />} label="BSW design only" />
            <FormControlLabel value="tdp_bsw" control={<Radio />} label="TDP and BSW design" />
          </RadioGroup>
          <FormControlLabel
            control={<Checkbox checked={includeCompleted} onChange={(event) => setIncludeCompleted(event.target.checked)} />}
            label={`Include ${projectToProcess?.completed_count ?? 0} completed record(s)`}
            disabled={!projectToProcess?.completed_count}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProjectToProcess(null)} disabled={Boolean(processingId)}>Cancel</Button>
          <Button variant="contained" onClick={submitProjectProcessing} disabled={Boolean(processingId)} sx={primaryButtonSx}>
            Queue records
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------- New Project Wizard ---------- */}
      <Dialog open={wizardOpen} onClose={closeWizard} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, color: pagePalette.ink }}>Create New Project</DialogTitle>
        <DialogContent dividers>
          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            {wizardSteps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {wizardBusy && <LinearProgress sx={{ mb: 2 }} />}
          {wizardError && <Alert severity="error" sx={{ mb: 2 }}>{wizardError}</Alert>}

          {activeStep === 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Project Name"
                required
                value={newName}
                onChange={(event) => setNewName(event.target.value)}
                fullWidth
                autoFocus
              />
              <TextField
                label="Description (optional)"
                value={newDescription}
                onChange={(event) => setNewDescription(event.target.value)}
                fullWidth
                multiline
                minRows={2}
              />
            </Box>
          )}

          {activeStep === 1 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Typography sx={{ color: pagePalette.muted, fontSize: "0.9rem" }}>
                Upload an Excel (.xlsx) file of records for <b>{createdProject?.name}</b>. You can skip this and upload later.
              </Typography>
              <Button
                component="label"
                variant="outlined"
                startIcon={<UploadFileIcon />}
                sx={secondaryButtonSx}
              >
                {selectedFile ? selectedFile.name : "Choose Excel file"}
                <input
                  type="file"
                  hidden
                  accept=".xlsx,.xlsm,.xltx,.xltm"
                  onChange={(event) => {
                    setSelectedFile(event.target.files?.[0] || null);
                    setUploadResult(null);
                  }}
                />
              </Button>
              {selectedFile && !uploadResult && (
                <Button variant="contained" onClick={handleUpload} disabled={wizardBusy} sx={primaryButtonSx}>
                  Upload & validate
                </Button>
              )}
              {uploadResult && (
                <Alert severity={uploadResult.error_count > 0 ? "warning" : "success"}>
                  <Typography sx={{ fontWeight: 700 }}>
                    {uploadResult.uploaded_count} record(s) uploaded
                    {uploadResult.error_count > 0 ? `, ${uploadResult.error_count} skipped` : ""}.
                  </Typography>
                  {uploadResult.error_count > 0 && Array.isArray(uploadResult.errors) && (
                    <Box sx={{ mt: 1, maxHeight: 120, overflow: "auto" }}>
                      {uploadResult.errors.slice(0, 5).map((err, idx) => (
                        <Typography key={idx} sx={{ fontSize: "0.78rem", color: pagePalette.muted }}>
                          Row {err.excel_row}: {err.error}
                        </Typography>
                      ))}
                      {uploadResult.errors.length > 5 && (
                        <Typography sx={{ fontSize: "0.78rem", color: pagePalette.muted }}>
                          …and {uploadResult.errors.length - 5} more.
                        </Typography>
                      )}
                    </Box>
                  )}
                </Alert>
              )}
            </Box>
          )}

          {activeStep === 2 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography sx={{ color: pagePalette.ink, fontWeight: 700 }}>
                {uploadResult?.uploaded_count
                  ? `${uploadResult.uploaded_count} record(s) are ready in "${createdProject?.name}".`
                  : `Project "${createdProject?.name}" is ready.`}
              </Typography>
              <Typography sx={{ color: pagePalette.muted, fontSize: "0.9rem", mb: 1 }}>
                How would you like to proceed?
              </Typography>
              <Typography sx={{ color: pagePalette.ink, fontWeight: 700 }}>Processing</Typography>
              <RadioGroup value={processingMode} onChange={(event) => setProcessingMode(event.target.value)}>
                <FormControlLabel value="tdp_only" control={<Radio />} label="TDP only" />
                <FormControlLabel value="bsw_only" control={<Radio />} label="BSW design only" />
                <FormControlLabel value="tdp_bsw" control={<Radio />} label="TDP and BSW design" />
              </RadioGroup>
              <RadioGroup value={processChoice} onChange={(event) => setProcessChoice(event.target.value)}>
                <FormControlLabel
                  value="now"
                  control={<Radio />}
                  label="Process now — queue all records for the design worker"
                />
                <FormControlLabel
                  value="later"
                  control={<Radio />}
                  label="Process later — just save the records"
                />
              </RadioGroup>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeWizard} disabled={wizardBusy} sx={secondaryButtonSx}>
            Cancel
          </Button>
          {activeStep === 0 && (
            <Button variant="contained" onClick={handleCreateProject} disabled={wizardBusy} sx={primaryButtonSx}>
              Create & continue
            </Button>
          )}
          {activeStep === 1 && (
            <Button variant="contained" onClick={() => setActiveStep(2)} disabled={wizardBusy} sx={primaryButtonSx}>
              {uploadResult ? "Continue" : "Skip for now"}
            </Button>
          )}
          {activeStep === 2 && (
            <Button variant="contained" onClick={handleFinish} disabled={wizardBusy} sx={primaryButtonSx}>
              Finish
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
